import { z } from 'zod'
import axios from 'axios'
import { useSuspenseQuery } from '@tanstack/react-query'
import { TeamLogoSchema } from '../assets/api'

// Rounds API
const RoundSchema = z.object({
  round_number: z.number(),
  round_start: z.string(),
  status: z.enum(['ended', 'live', 'upcoming']),
  is_live: z.boolean(),
  is_latest: z.boolean(),
})

export type Round = z.infer<typeof RoundSchema>

const getCompetitionRounds = async ({
  competition_id,
}: {
  competition_id: number
}) => {
  const response = await axios.get(`/api/competitions/${competition_id}/rounds`)
  return z.array(RoundSchema).parse(response.data)
}

export const useCompetitionRounds = ({
  competition_id,
}: {
  competition_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['competitionRounds', { competition_id }],
    queryFn: () => getCompetitionRounds({ competition_id }),
  })
}

type MatchQueryParams = {
  competition_id: number
  division?: number
  team_id?: number
  other_team_id?: number
  round_start?: number
  round_end?: number
  round_number?: number
}

const getCompetitionMatches = async (params: MatchQueryParams) => {
  const response = await axios.get(
    `/api/competitions/${params.competition_id}/matches`,
    {
      params,
    }
  )
  return z.array(MatchSchema).parse(response.data)
}

export const useMatches = (params: MatchQueryParams) => {
  return useSuspenseQuery({
    queryKey: ['competitionMatches', params],
    queryFn: () => getCompetitionMatches(params),
  })
}

const getSingleMatch = async ({
  competition_id,
  match_id,
}: {
  competition_id: number
  match_id: number
}) => {
  const response = await axios.get(
    `/api/competitions/${competition_id}/matches/${match_id}`
  )
  return MatchSchema.parse(response.data)
}

export const useSingleMatch = ({
  competition_id,
  match_id,
}: {
  competition_id: number
  match_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['singleMatch', { competition_id, match_id }],
    queryFn: () => getSingleMatch({ competition_id, match_id }),
  })
}

const getTeamMatchHistory = async ({
  competition_id,
  team1_id,
  team2_id,
}: {
  competition_id: number
  team1_id: number
  team2_id: number
}) => {
  const response = await axios.get(
    `/api/competitions/${competition_id}/teams/${team1_id}/head-to-head/${team2_id}`
  )
  return TeamMatchHistorySchema.parse(response.data)
}

export const useTeamMatchHistory = ({
  competition_id,
  team1_id,
  team2_id,
}: {
  competition_id: number
  team1_id: number
  team2_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['teamMatchHistory', { competition_id, team1_id, team2_id }],
    queryFn: () => getTeamMatchHistory({ competition_id, team1_id, team2_id }),
  })
}

export const RoundStatusSchema = z.enum(['ended', 'live', 'upcoming'])

export const MatchSchema = z.object({
  match_id: z.number(),
  home_team_id: z.number(),
  home_team_name: z.string(),
  home_team_score: z.number(),
  home_team_logo: TeamLogoSchema,
  // Away team can be null - it's a bye match (when odd number of teams in competition)
  away_team_id: z.number().nullable(),
  away_team_name: z.string(),
  away_team_score: z.number(),
  away_team_logo: TeamLogoSchema,
  round_number: z.number(),
  round_start: z.string(),
  round_status: RoundStatusSchema,
  video: z
    .object({
      main: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
})

export const TeamMatchHistorySchema = z.object({
  team1_id: z.number(),
  team2_id: z.number(),
  team1_name: z.string(),
  team2_name: z.string(),
  team1_logo: TeamLogoSchema,
  team2_logo: TeamLogoSchema,
  team1_rank: z.number().nullable(),
  team2_rank: z.number().nullable(),
  team1_rating: z.number(),
  team2_rating: z.number(),
  team1_wins: z.number(),
  team1_rank_change: z.number().nullable(),
  team2_rank_change: z.number().nullable(),
  draws: z.number(),
  team1_losses: z.number(),
  total_matches: z.number(),
})

// Match Stats API
const KpiStatSchema = z.object({
  metric_id: z.number(),
  metric_key: z.string(),
  metric_name: z.string(),
  home_team_percent: z.number(),
  away_team_percent: z.number(),
  home_team_score: z.number(),
  away_team_score: z.number(),
})

export const MatchStatsSchema = z.object({
  match_id: z.number(),
  home_team_name: z.string(),
  away_team_name: z.string().nullable(),
  home_team_score: z.number(),
  away_team_score: z.number(),
  home_team_match_points: z.number(),
  away_team_match_points: z.number(),
  home_mvp_id: z.number().nullable(),
  away_mvp_id: z.number().nullable(),
  match_status: z.string(),
  round_start: z.string(),
  kpi_stats: z.array(KpiStatSchema),
})

const getMatchStats = async ({
  competition_id,
  match_id,
}: {
  competition_id: number
  match_id: number
}) => {
  const response = await axios.get(
    `/api/competitions/${competition_id}/matches/${match_id}/stats`
  )
  return MatchStatsSchema.parse(response.data)
}

export const useMatchStats = ({
  competition_id,
  match_id,
}: {
  competition_id: number
  match_id: number
}) => {
  return useSuspenseQuery({
    queryKey: ['matchStats', { competition_id, match_id }],
    queryFn: () => getMatchStats({ competition_id, match_id }),
  })
}

export type Match = z.infer<typeof MatchSchema>
export type RoundStatus = z.infer<typeof RoundStatusSchema>
export type TeamMatchHistory = z.infer<typeof TeamMatchHistorySchema>
export type MatchStats = z.infer<typeof MatchStatsSchema>
export type KpiStat = z.infer<typeof KpiStatSchema>
